import type { ICellRendererParams } from 'ag-grid-community';
import type { ReactNode } from 'react';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { UrlActivityEntry } from '../api/types';
import { formatRelativeFa, formatTime } from '../lib/datetime';

function kindLabel(kind: UrlActivityEntry['kind']) {
  if (kind === 'inspect') return 'بازرسی';
  if (kind === 'google_index') return 'ایندکس گوگل';
  return 'IndexNow';
}

function kindSubtleTone(
  kind: UrlActivityEntry['kind'],
): 'info' | 'primary' | 'secondary' {
  if (kind === 'inspect') return 'info';
  if (kind === 'google_index') return 'primary';
  return 'secondary';
}

function CellCenter({ children }: { children: ReactNode }) {
  return (
    <div className="d-flex align-items-center w-100 h-100 min-w-0">
      {children}
    </div>
  );
}

export function urlActivityColumnConfig() {
  return [
    {
      field: 'kind',
      title: 'نوع',
      type: 'string',
      width: 160,
      minWidth: 160,
      cellRenderer: (params: ICellRendererParams<UrlActivityEntry>) => {
        const entry = params.data;
        if (!entry) return null;
        return (
          <CellCenter>
            <div className="d-flex flex-column gap-1 min-w-0">
              <SubtleBadge
                bg={kindSubtleTone(entry.kind)}
                pill
                className="align-self-start fs--2 text-nowrap"
                style={{ lineHeight: 1.35 }}
              >
                {kindLabel(entry.kind)}
              </SubtleBadge>
            </div>
          </CellCenter>
        );
      },
    },
    {
      field: 'success',
      title: 'نتیجه',
      type: 'string',
      width: 120,
      cellRenderer: (params: ICellRendererParams<UrlActivityEntry>) => {
        const entry = params.data;
        if (!entry) return null;
        return (
          <CellCenter>
            <SubtleBadge
              bg={entry.success ? 'success' : 'danger'}
              pill
              className="fs--2"
              style={{ lineHeight: 1.35 }}
            >
              {entry.success ? 'موفق' : 'ناموفق'}
            </SubtleBadge>
          </CellCenter>
        );
      },
    },
    {
      field: 'detail',
      title: 'جزئیات',
      type: 'string',
      flex: 1.4,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams<UrlActivityEntry>) => {
        const row = params.data;
        const detail = row?.detail;
        if (!detail) {
          return (
            <CellCenter>
              <span className="text-secondary">—</span>
            </CellCenter>
          );
        }
        return (
          <CellCenter>
            <div className="if-table-detail" title={detail}>
              {detail}
            </div>
          </CellCenter>
        );
      },
    },
    {
      field: 'createdAt',
      title: 'زمان',
      type: 'string',
      width: 220,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams<UrlActivityEntry>) => {
        const entry = params.data;
        if (!entry) return null;
        const absolute = formatTime(entry.createdAt);
        return (
          <CellCenter>
            <span className="text-nowrap">
              {formatRelativeFa(entry.createdAt)}
              {absolute ? (
                <span className="text-secondary"> ({absolute})</span>
              ) : null}
            </span>
          </CellCenter>
        );
      },
    },
  ];
}
